<template>
  <div>
    <div class="page-head">
      <div>
        <h2 class="page-title">门店库</h2>
        <p class="page-sub">共 {{ pagination.total }} 家门店</p>
      </div>
    </div>

    <DataTable
      :columns="columns"
      :data="storeList"
      :loading="tableLoading"
      :total="pagination.total"
      :page="pagination.currentPage"
      :page-size="pagination.pageSize"
      row-key="storeEntCode"
      empty-text="没有匹配的门店"
      @update:page="handleCurrentChange"
      @update:page-size="handleSizeChange"
      @refresh="getStoreList"
    >
      <template #filters>
        <div class="filter-bar">
          <div class="filter-inline">
            <label>关键词</label>
            <el-input
              v-model="searchKeyword"
              placeholder="门店名称或企业编码"
              clearable
              style="width: 240px"
              @keyup.enter="handleSearch"
            />
          </div>
          <div class="filter-inline">
            <label>开店模式</label>
            <el-select v-model="storePattern" placeholder="全部" clearable style="width: 130px">
              <el-option v-for="p in patternOptions" :key="p" :label="p" :value="p" />
            </el-select>
          </div>
          <button class="btn" @click="handleReset">重置</button>
          <button class="btn btn-primary" @click="handleSearch">查询</button>
        </div>
      </template>

      <template #cell-storeName="{ row }">
        <a
          v-if="hasPermission('store:detail')"
          class="dt-link"
          @click="goDetail(row.storeEntCode)"
        >{{ row.storeName }}</a>
        <span v-else>{{ row.storeName }}</span>
      </template>

      <template #cell-region="{ row }">
        {{ [row.province, row.city].filter(Boolean).join(' ') || '—' }}
      </template>

      <template #ops="{ row }">
        <button class="dt-op" @click="goDetail(row.storeEntCode)">详情</button>
      </template>
    </DataTable>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import DataTable from '@/components/DataTable.vue'
import assetService from '@/services/assetService'
import store from '@/store'

export default {
  name: 'StoreList',
  components: { DataTable },
  setup() {
    const router = useRouter()

    const storeList = ref([])
    const tableLoading = ref(false)
    const searchKeyword = ref('')
    const storePattern = ref('')

    const pagination = reactive({ currentPage: 1, pageSize: 20, total: 0 })

    const columns = [
      { prop: 'storeName', label: '门店简称', minWidth: 150 },
      { prop: 'storeEntCode', label: '门店企业编码', minWidth: 150 },
      { prop: 'storeEntName', label: '门店企业名称', minWidth: 190 },
      { prop: 'storePattern', label: '开店模式', width: 110 },
      { prop: 'region', label: '所在地区', minWidth: 150 },
      { prop: 'brandCode', label: '品牌编码', width: 120 },
      { prop: 'rentTerm', label: '租期(天)', width: 100, type: 'number', align: 'right' }
    ]

    const patternOptions = computed(() => {
      const set = new Set(storeList.value.map(s => s.storePattern).filter(Boolean))
      return [...set]
    })

    const permissionCodes = computed(() => {
      const permissions = store.state.permissions
      return permissions ? Object.values(permissions).map(p => p.permissionCode) : []
    })

    const hasPermission = (code) => permissionCodes.value.includes(code)

    const getStoreList = async () => {
      try {
        tableLoading.value = true
        const params = {
          pageNum: pagination.currentPage,
          pageSize: pagination.pageSize,
          keyword: searchKeyword.value
        }
        const response = await assetService.getStoreList(params)
        let list = response?.data?.storeList || []
        if (storePattern.value) {
          list = list.filter(s => s.storePattern === storePattern.value)
        }
        storeList.value = list
        pagination.total = response?.data?.size || 0
      } catch (error) {
        ElMessage.error('获取门店列表失败：' + (error?.message || ''))
      } finally {
        tableLoading.value = false
      }
    }

    const handleSearch = () => {
      pagination.currentPage = 1
      getStoreList()
    }

    const handleReset = () => {
      searchKeyword.value = ''
      storePattern.value = ''
      handleSearch()
    }

    const handleCurrentChange = (page) => {
      pagination.currentPage = page
      getStoreList()
    }

    const handleSizeChange = (size) => {
      pagination.pageSize = size
      pagination.currentPage = 1
      getStoreList()
    }

    const goDetail = (code) => {
      router.push({ name: 'storeDetail', params: { storeEntCode: code } })
    }

    onMounted(getStoreList)

    return {
      storeList, tableLoading, searchKeyword, storePattern, pagination, columns,
      patternOptions, hasPermission, handleSearch, handleReset,
      handleCurrentChange, handleSizeChange, goDetail, getStoreList
    }
  }
}
</script>

<style scoped>
.page-head {
  display: flex;
  align-items: flex-end;
  margin-bottom: 16px;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: var(--text-primary);
}

.page-sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--text-tertiary);
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-inline {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-inline label {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.btn {
  padding: 6px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn-primary {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: #fff;
}

.btn-primary:hover {
  background: var(--brand-600);
  border-color: var(--brand-600);
  color: #fff;
}

.dt-link {
  color: var(--brand-500);
  cursor: pointer;
  text-decoration: none;
}

.dt-link:hover {
  text-decoration: underline;
}
</style>
